import {
  formatDuration,
  SOCIAL_FORMAT_LABELS,
  SOCIAL_NETWORK_LABELS,
  SOCIAL_PIECE_STATUS_LABELS,
  SOCIAL_PRIORITY_LABELS,
  SOCIAL_STEP_LABELS,
  socialPlanSchema,
  type SocialNetwork,
  type SocialPiece,
  type SocialPieceStep,
  type SocialPlan,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Textarea,
} from '@growup/ui';
import { useState } from 'react';

import { messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { PieceForm } from './PieceForm';
import { PIECE_TONE, PLANS } from './PlansView';

const dmy = (day: string) => day.split('-').reverse().join('/');

interface Props {
  plan: SocialPlan;
  piece: SocialPiece;
  me: string;
  onChange: (plan: SocialPlan) => void;
  onClose: () => void;
}

/**
 * Uma peca: os dados (o gestor muda) e os passos Filmagem, Edicao, Aprovacao
 * e Publicacao, com quem fez e os links (RF-RS7, RF-RS8).
 */
export function PieceView({ plan, piece, me, onChange, onClose }: Props) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const base = `${PLANS}/${plan.id}/pieces/${piece.id}`;
  const abandoned = piece.status === 'abandoned';

  /** Corre um pedido e mostra o plano que volta; `false` se falhou. */
  const run = async (method: string, path: string, body?: unknown): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      onChange(socialPlanSchema.parse(await send(method, `${base}${path}`, body)));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const abandon = () => {
    if (abandoned) {
      void run('POST', '/abandon', { abandoned: false });
      return;
    }
    const reason = window.prompt('Porque se abandona esta peça? (opcional)', '');
    if (reason !== null) void run('POST', '/abandon', { abandoned: true, reason });
  };

  const remove = async () => {
    if (!window.confirm(`Apagar a peça «${piece.title}»? Não se pode desfazer.`)) return;
    if (await run('DELETE', '')) onClose();
  };

  // "Quem fez": eu e os influencers do plano.
  const people = plan.members.some((m) => m.accountId === me)
    ? plan.members
    : [{ accountId: me, login: 'Eu' }, ...plan.members];

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← {plan.name}
        </Button>
      </div>
      <Card
        headingLevel={2}
        title={piece.title}
        actions={
          plan.canManage && !editing ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                Editar
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={abandon}>
                {abandoned ? 'Retomar' : 'Abandonar'}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void remove()}>
                Apagar
              </Button>
            </div>
          ) : null
        }
      >
        {editing ? (
          <PieceForm
            piece={piece}
            event={plan.event}
            zones={plan.zones}
            submitLabel="Guardar"
            onCancel={() => setEditing(false)}
            onSubmit={async (payload) => {
              onChange(socialPlanSchema.parse(await send('PUT', base, payload)));
              setEditing(false);
            }}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <p className="flex flex-wrap items-center gap-2">
              <StatusBadge tone={PIECE_TONE[piece.status]}>
                {SOCIAL_PIECE_STATUS_LABELS[piece.status]}
              </StatusBadge>
              <span className="text-caption text-gu-ink-muted">
                {SOCIAL_FORMAT_LABELS[piece.format]}
                {piece.durationSeconds ? ` · ${formatDuration(piece.durationSeconds)}` : ''} ·{' '}
                {SOCIAL_PRIORITY_LABELS[piece.priority]}
                {piece.publishOn ? ` · publicar a ${dmy(piece.publishOn)}` : ''}
                {piece.eventDay ? ` · no evento a ${dmy(piece.eventDay)}` : ''}
                {piece.zone ? ` · ${piece.zone.name}` : ''}
              </span>
            </p>
            {abandoned && piece.abandonReason ? <p>Abandonada: {piece.abandonReason}</p> : null}
            {piece.description ? <p className="whitespace-pre-line">{piece.description}</p> : null}
            {piece.referenceUrls.length > 0 ? (
              <ul className="text-caption">
                {piece.referenceUrls.map((u) => (
                  <li key={u}>
                    <a href={u} target="_blank" rel="noreferrer" className="underline">
                      {u}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
            {piece.notes ? <p className="text-gu-ink-muted">{piece.notes}</p> : null}
          </div>
        )}
      </Card>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {piece.steps.map((step) => (
        <StepCard
          key={`${step.step}-${String(step.doneAt)}`}
          plan={plan}
          piece={piece}
          step={step}
          people={people}
          me={me}
          busy={busy}
          disabled={abandoned}
          run={run}
        />
      ))}
    </div>
  );
}

function StepCard({
  plan,
  piece,
  step,
  people,
  me,
  busy,
  disabled,
  run,
}: {
  plan: SocialPlan;
  piece: SocialPiece;
  step: SocialPieceStep;
  people: { accountId: string; login: string }[];
  me: string;
  busy: boolean;
  disabled: boolean;
  run: (method: string, path: string, body?: unknown) => Promise<boolean>;
}) {
  const [assignee, setAssignee] = useState(step.assignee?.accountId ?? '');
  const [dueOn, setDueOn] = useState(step.dueOn ?? '');
  const [notApplicable, setNotApplicable] = useState(step.notApplicable);
  const [linkUrl, setLinkUrl] = useState(step.linkUrl);
  const [notes, setNotes] = useState(step.notes);
  const [doneBy, setDoneBy] = useState(me);
  const label = SOCIAL_STEP_LABELS[step.step];
  const byNetwork = step.step === 'publishing' && piece.networks.length > 0;
  const planChanged =
    assignee !== (step.assignee?.accountId ?? '') ||
    dueOn !== (step.dueOn ?? '') ||
    notApplicable !== step.notApplicable;

  const mark = (done: boolean) =>
    void run('POST', `/steps/${step.step}/done`, {
      done,
      doneByAccountId: doneBy === me ? null : doneBy,
      linkUrl,
      notes,
    });

  return (
    <Card headingLevel={3} title={label}>
      <div className="flex flex-col gap-3">
        <p className="text-caption">
          {step.notApplicable
            ? 'Não se aplica a esta peça.'
            : step.done
              ? `Feito por ${step.doneBy ?? '—'}${step.doneAt ? ` a ${formatDateTime(step.doneAt)}` : ''}${step.markedBy ? ` (marcado por ${step.markedBy})` : ''}.`
              : 'Por fazer.'}
          {step.assignee ? ` Responsável: ${step.assignee.login}.` : ''}
          {step.dueOn ? ` Prazo: ${dmy(step.dueOn)}.` : ''}
        </p>
        {step.overdue ? <Notice tone="warning">O prazo já passou.</Notice> : null}
        {step.outOfOrder ? (
          <Notice tone="warning">Feito antes de um passo anterior estar feito.</Notice>
        ) : null}
        {step.step === 'approval' && step.reviewNote && !step.done ? (
          <Notice tone="info">Alterações pedidas: {step.reviewNote}</Notice>
        ) : null}

        {plan.canManage ? (
          <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto_auto]">
            <Field label="Responsável" optional>
              <Select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">—</option>
                {plan.members.map((m) => (
                  <option key={m.accountId} value={m.accountId}>
                    {m.login}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Prazo" optional>
              <DateInput value={dueOn} onChange={setDueOn} />
            </Field>
            {step.step !== 'publishing' ? (
              <Checkbox
                label="Não se aplica"
                checked={notApplicable}
                onChange={(e) => setNotApplicable(e.target.checked)}
              />
            ) : (
              <span />
            )}
            <Button
              variant="secondary"
              disabled={busy || !planChanged}
              onClick={() =>
                void run('PUT', `/steps/${step.step}`, {
                  assigneeAccountId: assignee === '' ? null : assignee,
                  dueOn: dueOn === '' ? null : dueOn,
                  notApplicable,
                })
              }
            >
              Guardar {label.toLowerCase()}
            </Button>
          </div>
        ) : null}

        {step.notApplicable || disabled ? null : step.step === 'approval' ? (
          plan.canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy || step.done}
                onClick={() => void run('POST', '/review', { approved: true })}
              >
                Aprovar
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  const note = window.prompt('Que alterações pede? A peça volta à Edição.', '');
                  if (note !== null) void run('POST', '/review', { approved: false, note });
                }}
              >
                Pedir alterações
              </Button>
            </div>
          ) : null
        ) : byNetwork ? (
          <Publications piece={piece} people={people} me={me} busy={busy} run={run} />
        ) : (
          <div className="flex flex-col gap-3">
            {step.step !== 'publishing' ? (
              <Field
                label={step.step === 'filming' ? 'Ficheiros em bruto' : 'Vídeo final'}
                optional
                hint="Link https:// para a pasta ou o ficheiro (Drive, OneDrive…)."
              >
                <Input
                  value={linkUrl}
                  maxLength={500}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </Field>
            ) : null}
            <Field label="Notas" optional>
              <Textarea
                rows={2}
                value={notes}
                maxLength={2000}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Feito por">
                <Select value={doneBy} onChange={(e) => setDoneBy(e.target.value)}>
                  {people.map((p) => (
                    <option key={p.accountId} value={p.accountId}>
                      {p.login}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button disabled={busy} onClick={() => mark(true)}>
                {step.done ? 'Guardar' : 'Marcar como feito'}
              </Button>
              {step.done ? (
                <Button variant="ghost" disabled={busy} onClick={() => mark(false)}>
                  Desmarcar
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function Publications({
  piece,
  people,
  me,
  busy,
  run,
}: {
  piece: SocialPiece;
  people: { accountId: string; login: string }[];
  me: string;
  busy: boolean;
  run: (method: string, path: string, body?: unknown) => Promise<boolean>;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-caption text-gu-ink-muted">
        Um link por rede. Com todas, a publicação fica feita ({piece.publications.length} de{' '}
        {piece.networks.length} redes).
      </p>
      {piece.networks.map((n) => (
        <PublicationRow
          key={`${n}-${piece.publications.find((p) => p.network === n)?.url ?? ''}`}
          network={n}
          piece={piece}
          people={people}
          me={me}
          busy={busy}
          run={run}
        />
      ))}
    </div>
  );
}

function PublicationRow({
  network,
  piece,
  people,
  me,
  busy,
  run,
}: {
  network: SocialNetwork;
  piece: SocialPiece;
  people: { accountId: string; login: string }[];
  me: string;
  busy: boolean;
  run: (method: string, path: string, body?: unknown) => Promise<boolean>;
}) {
  const current = piece.publications.find((p) => p.network === network);
  const [url, setUrl] = useState(current?.url ?? '');
  const [by, setBy] = useState(me);
  const name = SOCIAL_NETWORK_LABELS[network];
  return (
    <div className="flex flex-col gap-2 border-2 border-gu-line p-2">
      <div className="grid items-end gap-2 sm:grid-cols-[1fr_12rem_auto]">
        <Field label={`Post no ${name}`} hint="https://…">
          <Input value={url} maxLength={500} onChange={(e) => setUrl(e.target.value)} />
        </Field>
        <Field label="Publicado por">
          <Select value={by} onChange={(e) => setBy(e.target.value)}>
            {people.map((p) => (
              <option key={p.accountId} value={p.accountId}>
                {p.login}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex gap-1">
          <Button
            disabled={busy || url.trim() === ''}
            onClick={() =>
              void run('PUT', `/publications/${network}`, {
                url,
                publishedByAccountId: by === me ? null : by,
              })
            }
          >
            Guardar
          </Button>
          {current ? (
            <Button
              variant="ghost"
              disabled={busy}
              aria-label={`Tirar o link do ${name}`}
              onClick={() => void run('DELETE', `/publications/${network}`)}
            >
              ✕
            </Button>
          ) : null}
        </div>
      </div>
      {current ? (
        <p className="text-caption">
          ✓{' '}
          <a href={current.url} target="_blank" rel="noreferrer" className="underline">
            Publicado
          </a>{' '}
          por {current.publishedBy} a {formatDateTime(current.publishedAt)}
        </p>
      ) : null}
    </div>
  );
}
